import React, { useState } from "react";
import { Check, AlertTriangle, Target, UsersRound, ShieldCheck, ServerCog, Scale, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface LensItem {
  id: number;
  icon: React.ElementType;
  lensLabel: string;
  lensTitle: string;
  question: string;
  thesis: string;
  guidelines: string[];
  failureTitle: string;
  failureText: string;
}

const LENSES: LensItem[] = [
  {
    id: 1,
    icon: Target,
    lensLabel: "Test 1",
    lensTitle: "Business Fit",
    question: "Is this worth building, or merely safe enough to approve?",
    thesis: "Build once. Start narrow. Make the first slice production-grade.",
    guidelines: [
      "Targets a material business outcome customers, users, or leadership actually care about.",
      "Builds a narrow technical slice in the real production environment.",
      "Tests whether the system can create measurable value before expanding scope."
    ],
    failureTitle: "Common Failure Mode",
    failureText: "The Disposable POC: A safe demo is built outside the real operating environment, proves little, and must be rebuilt before it can create business value."
  },
  {
    id: 2,
    icon: UsersRound,
    lensLabel: "Test 2",
    lensTitle: "User Reality",
    question: "Will real users adopt this, or is it an executive fantasy with a login screen?",
    thesis: "Talk to the people who will actually live with the system.",
    guidelines: [
      "Validates the workflow with internal users, customers, clients, or frontline teams.",
      "Separates leadership assumptions from actual user behavior, trust, incentives, and friction.",
      "Identifies adoption blockers before engineering effort turns into sunk cost."
    ],
    failureTitle: "Common Failure Mode",
    failureText: "The Boardroom Product: A system is designed around internal enthusiasm instead of real user input, then dies quietly through workarounds, avoidance, and non-adoption."
  },
  {
    id: 3,
    icon: ShieldCheck,
    lensLabel: "Test 3",
    lensTitle: "System Integrity",
    question: "Does this behave reliably when the data, users, and edge cases get messy?",
    thesis: "Trust comes from constraints, validation, and repeatable behavior.",
    guidelines: [
      "Replaces fragile prompt chains with typed inputs, schema constraints, validation layers, and deterministic control points.",
      "Tests against real edge cases, ambiguous requests, bad inputs, and adversarial behavior.",
      "Separates model judgment from business rules so the system can be inspected, corrected, and trusted."
    ],
    failureTitle: "Common Failure Mode",
    failureText: "The Demo Trap: A prototype works in staged conditions, then breaks when exposed to live data, changing context, unclear instructions, and actual humans."
  },
  {
    id: 4,
    icon: ServerCog,
    lensLabel: "Test 4",
    lensTitle: "Production Readiness",
    question: "Can this run securely inside the actual operating environment?",
    thesis: "If it cannot be operated, monitored, and maintained, it is not production-ready.",
    guidelines: [
      "Reviews access controls, deployment boundaries, logging, monitoring, data handling, and fallback behavior.",
      "Identifies unmanaged APIs, shadow tools, third-party model exposure, and hidden data flows.",
      "Confirms the system can be maintained without becoming bespoke technical debt."
    ],
    failureTitle: "Common Failure Mode",
    failureText: "The Shadow AI Leak: Teams route sensitive data, client records, or proprietary workflows through unmanaged tools, creating security, IP, and compliance exposure."
  },
  {
    id: 5,
    icon: Scale,
    lensLabel: "Test 5",
    lensTitle: "Governance Risk",
    question: "Can the organization defend what the AI system does and who is accountable?",
    thesis: "Accountability has to be designed before something goes wrong.",
    guidelines: [
      "Maps AI decisions, recommendations, and automated actions to ownership, review, and escalation paths.",
      "Establishes audit trails, approval boundaries, and controls for high-impact workflows.",
      "Reduces dependency on opaque vendors, undocumented prompts, and institutional memory trapped in someone’s head."
    ],
    failureTitle: "Common Failure Mode",
    failureText: "The Accountability Gap: Critical AI risk is delegated to vendors, IT, or “the model,” leaving no owner when the system causes harm, exposes data, or makes the wrong call."
  }
];

export default function QASection() {
  const [activeTab, setActiveTab] = useState<number>(1);

  const currentLens = LENSES.find((l) => l.id === activeTab) || LENSES[0];
  const Icon = currentLens.icon;

  // Helper to handle smooth scroll to diagnostic intake
  const scrollToContact = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
    setTimeout(() => {
      const target = document.querySelector("#advisory-intake-form");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <section id="qa" className="bg-brand-cream/40 py-20 md:py-28 border-b border-brand-sand relative">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-charcoal font-display">
            Five Tests Before AI Enters Production
          </h2>
          <p className="text-xs sm:text-sm text-stone-700 font-sans max-w-2xl mx-auto leading-relaxed mt-3">
            Before I recommend building or scaling an AI system, I test whether the problem matters, users will adopt it, the system behaves reliably, the deployment can survive real operations, and accountability is clear.
          </p>
        </div>

        {/* Interactive Dashboard Layout */}
        <div className="w-full">
          
          {/* Tabs Container: Responsive layout (horizontally scrollable on mobile, grid on desktop) */}
          <div className="flex md:grid md:grid-cols-5 overflow-x-auto scrollbar-none md:overflow-visible gap-1.5 md:gap-0 -mb-px relative z-10 select-none pb-2 md:pb-0">
            {LENSES.map((lens) => {
              const isActive = activeTab === lens.id;
              const TabIcon = lens.icon;
              return (
                <button
                  key={lens.id}
                  onClick={() => setActiveTab(lens.id)}
                  className={`flex-shrink-0 w-[140px] md:w-auto text-left px-4 py-3 border rounded-t-sm transition-all duration-200 cursor-pointer ${
                    isActive
                      ? "bg-[#C2CCB1] text-brand-espresso border-stone-400 font-medium"
                      : "bg-white text-stone-700 hover:text-stone-900 hover:bg-stone-50 border-brand-sand border-b-stone-300 md:border-b-transparent"
                  }`}
                >
                  <div className={`flex items-center gap-1.5 mb-0.5 ${isActive ? "opacity-90" : "text-stone-700 font-bold"}`}>
                    <TabIcon className="w-3.5 h-3.5 stroke-[1.5]" />
                    <span className="text-[10px] font-mono tracking-widest font-bold block uppercase">
                      {lens.lensLabel}
                    </span>
                  </div>
                  <div className="text-xs font-sans font-semibold truncate">
                    {lens.lensTitle}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tab Content Box */}
          <div className="bg-white border border-stone-300 md:border-t-stone-400 rounded-b-sm rounded-tr-sm shadow-md relative overflow-hidden min-h-[360px] flex flex-col justify-between">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="p-6 sm:p-10 flex-grow flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-2 mb-1 text-slate-500">
                    <Icon className="w-5 h-5 stroke-[1.5]" />
                    <div className="text-[10px] font-mono tracking-widest font-bold uppercase">
                      {currentLens.lensLabel}: {currentLens.lensTitle}
                    </div>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold font-display text-brand-charcoal leading-tight mt-2">
                    {currentLens.question}
                  </h3>
                  <p className="text-[11px] text-slate-600 font-medium tracking-wide mt-2 mb-8">
                    {currentLens.thesis}
                  </p>

                  {/* Positive Guidelines Checklist */}
                  <ul className="space-y-4 mb-8">
                    {currentLens.guidelines.map((line, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-green/10 text-brand-green shrink-0 mt-0.5">
                          <Check className="w-3 h-3 stroke-[3]" />
                        </span>
                        <span className="text-xs sm:text-sm text-stone-700 leading-relaxed font-sans">
                          {line}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Common AI Failure Box */}
                <div className="bg-[#F4F6F1] border border-stone-200 border-l-4 border-l-[#C2CCB1] p-4 sm:p-5 rounded-r-sm">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8C1F1F] mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-brand-orange" />
                    {currentLens.failureTitle}
                  </span>
                  <p className="text-xs sm:text-sm text-stone-600 leading-relaxed font-light font-sans">
                    {currentLens.failureText}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center border-t border-brand-sand/60 pt-10">
            <p className="text-base sm:text-lg text-stone-700 font-light mb-6 max-w-xl mx-auto leading-relaxed">
              If an AI initiative cannot pass these five tests, it is not ready for production spend.
            </p>
            <button
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-brand-burgundy hover:bg-brand-orange text-white font-mono font-bold uppercase tracking-wider text-xs py-3.5 px-6 rounded-sm transition-colors text-center cursor-pointer shadow-md inline-flex items-center justify-center gap-1.5"
            >
              Begin CAIO Diagnostic <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}
